import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import Header from './Header';
import Footer from './Footer';

const TermsConditions = ({ onOpenForm }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Helmet>
        <title>Terms & Conditions | Register Business Name Trademark</title>
        <meta 
          name="description" 
          content="Read our terms and conditions for trademark registration services. Understand service agreements, fees, client responsibilities, and limitations for USPTO trademark filing." 
        />
        <meta name="robots" content="index, follow" />
        <link rel="canonical" href="https://registerbusinessnametrademark.com/terms-conditions" />
      </Helmet>
      
      <Header onOpenForm={onOpenForm} />
      
      <main className="pt-32 pb-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Header */}
            <div className="mb-12">
              <h1 className="text-4xl md:text-5xl font-bold text-primary mb-4">Terms & Conditions</h1>
              <p className="text-gray-600 text-lg">Last Updated: December 2025</p>
            </div>

            {/* Content */}
            <div className="prose prose-lg max-w-none">
              {/* Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">1. Acceptance of Terms</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Welcome to Register Business Name Trademark. These Terms and Conditions ("Terms") govern your use of our website and trademark registration services. By accessing our website or using our services, you agree to be bound by these Terms.
                </p>
                <p className="text-gray-700 leading-relaxed">
                  If you do not agree to these Terms, please do not use our website or services. We reserve the right to modify these Terms at any time, and your continued use of our services constitutes acceptance of any changes.
                </p>
              </section>

              {/* Services Description */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">2. Services Description</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We provide trademark registration services including:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Comprehensive trademark searches</li>
                  <li>Trademark application preparation and filing with the USPTO</li>
                  <li>Office action response services</li>
                  <li>Trademark monitoring and enforcement</li>
                  <li>Trademark renewal services</li>
                  <li>Brand protection consultation</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  Our services are designed to assist with federal trademark registration in the United States. We do not guarantee approval of your trademark application, as final decisions rest with the USPTO.
                </p>
              </section>

              {/* Service Agreement */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">3. Service Agreement and Fees</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">3.1 Engagement</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  By purchasing our services, you enter into a service agreement with us. We will provide the services described in your selected package with professional care and expertise.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">3.2 Fees and Payment</h3>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Our service fees are separate from USPTO government filing fees</li>
                  <li>All fees must be paid in full before we file your application with the USPTO</li>
                  <li>Payment can be made via credit card, debit card, or other accepted payment methods</li>
                  <li>All fees are non-refundable once work has commenced, except as outlined in our refund policy</li>
                  <li>USPTO government fees are subject to change without notice</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">3.3 Additional Costs</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Additional fees may apply for:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Responding to USPTO office actions (if not included in your package)</li>
                  <li>Filing amendments or corrections</li>
                  <li>International trademark filings</li>
                  <li>Expedited services or rush processing</li>
                  <li>Additional classes of goods or services</li>
                </ul>
              </section>

              {/* Client Responsibilities */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">4. Client Responsibilities</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  As our client, you agree to:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Provide accurate, complete, and truthful information</li>
                  <li>Respond promptly to our requests for information or documentation</li>
                  <li>Review all documents and materials we prepare before filing</li>
                  <li>Notify us immediately of any changes to your contact information</li>
                  <li>Use your trademark in commerce if filing on a use-in-commerce basis</li>
                  <li>Maintain proper specimens showing trademark use</li>
                  <li>Comply with all USPTO requirements and deadlines</li>
                  <li>Pay all fees when due</li>
                </ul>
              </section>

              {/* Limitations and Disclaimers */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">5. Limitations and Disclaimers</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">5.1 No Guarantee of Registration</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  While we use our expertise to maximize the chances of successful trademark registration, we cannot guarantee that the USPTO will approve your application. The USPTO has sole authority to grant or refuse trademark registrations.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">5.2 Search Limitations</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our trademark searches are comprehensive but cannot identify all potential conflicts. Common law trademark rights, recent filings, and pending applications may not appear in search results. A clear search result does not guarantee registration approval.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">5.3 Legal Advice</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our services include trademark filing and related administrative services. While our team includes experienced trademark professionals, nothing we provide should be construed as legal advice unless explicitly stated in writing by a licensed attorney.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">5.4 Third-Party Rights</h3>
                <p className="text-gray-700 leading-relaxed">
                  We are not responsible for third-party claims of trademark infringement, opposition proceedings, or cancellation actions. You are responsible for ensuring your trademark does not infringe on existing rights.
                </p>
              </section>

              {/* Intellectual Property */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">6. Intellectual Property Rights</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">6.1 Your Trademarks</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You retain all rights to your trademarks. We do not claim any ownership interest in your marks, business names, logos, or other intellectual property.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">6.2 Our Website and Materials</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  All content on our website, including text, graphics, logos, images, and software, is our property or our licensors' property and is protected by copyright and trademark laws. You may not reproduce, distribute, or create derivative works without our written permission.
                </p>
              </section>

              {/* Confidentiality */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">7. Confidentiality</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We maintain strict confidentiality of all client information and trademark details. We will not disclose your information to third parties except:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>As necessary to provide our services (e.g., filing with USPTO)</li>
                  <li>With your express written consent</li>
                  <li>As required by law or legal process</li>
                  <li>To our service providers under confidentiality agreements</li>
                </ul>
                <p className="text-gray-700 leading-relaxed">
                  Please note that trademark applications filed with the USPTO become public records and can be searched by anyone.
                </p>
              </section>

              {/* Cancellation and Refunds */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">8. Cancellation and Refund Policy</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">8.1 Cancellation by Client</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You may cancel your service agreement before we file your application with the USPTO for a full refund minus a $50 administrative fee. Once we have filed your application with the USPTO, no refunds are available for our service fees or government fees.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">8.2 Cancellation by Us</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We reserve the right to refuse or cancel service if:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>You provide false or misleading information</li>
                  <li>You fail to cooperate or respond to our requests</li>
                  <li>Payment is declined or reversed</li>
                  <li>We determine the mark is not registrable</li>
                  <li>Continuing service would violate ethical or legal obligations</li>
                </ul>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">8.3 USPTO Fees</h3>
                <p className="text-gray-700 leading-relaxed">
                  USPTO government filing fees are non-refundable under any circumstances once paid to the USPTO.
                </p>
              </section>

              {/* Limitation of Liability */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">9. Limitation of Liability</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  TO THE MAXIMUM EXTENT PERMITTED BY LAW:
                </p>
                <ul className="list-disc pl-6 mb-4 text-gray-700 space-y-2">
                  <li>Our total liability for any claims arising from our services shall not exceed the amount you paid for those services</li>
                  <li>We shall not be liable for any indirect, incidental, special, consequential, or punitive damages</li>
                  <li>We are not liable for USPTO decisions, third-party infringement claims, or business losses</li>
                  <li>We are not responsible for missed deadlines due to client delays or failure to provide information</li>
                  <li>We are not liable for technical failures, internet outages, or force majeure events beyond our control</li>
                </ul>
              </section>

              {/* Indemnification */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">10. Indemnification</h2>
                <p className="text-gray-700 leading-relaxed">
                  You agree to indemnify, defend, and hold harmless Register Business Name Trademark, its officers, employees, and agents from any claims, damages, losses, liabilities, and expenses (including attorney fees) arising from: (a) your use of our services; (b) your violation of these Terms; (c) your violation of any third-party rights, including trademark infringement; or (d) any false or misleading information you provide.
                </p>
              </section>

              {/* Dispute Resolution */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">11. Dispute Resolution</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">11.1 Governing Law</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms shall be governed by and construed in accordance with the laws of the State of California, without regard to conflict of law principles.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">11.2 Arbitration</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Any disputes arising from these Terms or our services shall be resolved through binding arbitration in San Jose, California, rather than in court, except that you may assert claims in small claims court if they qualify.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">11.3 Class Action Waiver</h3>
                <p className="text-gray-700 leading-relaxed">
                  You agree to resolve disputes with us on an individual basis only and waive any right to participate in class action lawsuits or class-wide arbitration.
                </p>
              </section>

              {/* General Provisions */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">12. General Provisions</h2>
                
                <h3 className="text-xl font-semibold text-primary mb-3">12.1 Entire Agreement</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  These Terms, together with our Privacy Policy, constitute the entire agreement between you and us regarding our services.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">12.2 Severability</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If any provision of these Terms is found to be unenforceable, the remaining provisions shall remain in full force and effect.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">12.3 Waiver</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  Our failure to enforce any right or provision of these Terms shall not be deemed a waiver of such right or provision.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">12.4 Assignment</h3>
                <p className="text-gray-700 leading-relaxed mb-4">
                  You may not assign or transfer your rights or obligations under these Terms without our written consent. We may assign our rights and obligations without restriction.
                </p>

                <h3 className="text-xl font-semibold text-primary mb-3 mt-6">12.5 Amendments</h3>
                <p className="text-gray-700 leading-relaxed">
                  We reserve the right to modify these Terms at any time. Material changes will be notified through our website or via email. Your continued use of our services after changes constitutes acceptance.
                </p>
              </section>

              {/* Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-primary mb-4">13. Contact Us</h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  If you have questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-accent/30 rounded-lg p-6">
                  <p className="text-gray-700 mb-2"><strong>Register Business Name Trademark</strong></p>
                  <p className="text-gray-700 mb-2">360 S Market St, San Jose, CA 95113</p>
                  <p className="text-gray-700 mb-2">Email: info@registerbusinessnametrademark.com</p>
                  <p className="text-gray-700">Phone: +1 (123) 123-4567</p>
                </div>
              </section>

              {/* Acknowledgment */}
              <section className="mb-8">
                <div className="bg-secondary/10 border-l-4 border-secondary p-6 rounded">
                  <p className="text-gray-700 leading-relaxed font-semibold">
                    BY USING OUR SERVICES, YOU ACKNOWLEDGE THAT YOU HAVE READ, UNDERSTOOD, AND AGREE TO BE BOUND BY THESE TERMS AND CONDITIONS.
                  </p>
                </div>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer onOpenForm={onOpenForm} />
    </div>
  );
};

export default TermsConditions;
